/**
 * シーンID → BGM の対応表（#396 で `composables/useMusic.ts` から外部化）。
 *
 * 新しいエリア/ワンダーに BGM を付ける場合はここに 1 行足すだけでよい。
 * ファイル名は `public/musics/` 配下の実ファイルと **完全一致** させること
 * （大文字小文字も含む。例: `_80Kbps` と `_80kbps` が実ファイルで混在している）。
 *
 * 将来的に音楽設定を scene_master（seed）へ寄せる案もある
 * （area-wonder-workflow に「音楽ステップ」を組み込む motivation。#396 参照）が、
 * 本 Phase はフロントの data 化 + 型安全化に留める。
 */

export interface MusicEntry {
  /** `public/musics/` 配下のファイル名（拡張子込み・大文字小文字は厳密に一致させる） */
  filename: string
  /** ループ再生するか（未指定は true = ループ） */
  loop?: boolean
  /** 音量 0.0–1.0（未指定は {@link DEFAULT_MUSIC_VOLUME}） */
  volume?: number
}

/** BGM の既定音量（従来 `useMusic.ts` にハードコードされていた 0.3 を定数化） */
export const DEFAULT_MUSIC_VOLUME = 0.3

/**
 * シーンID → 音楽ファイル。値は文字列（既定でループ）か {@link MusicEntry}。
 *
 * `as const satisfies ...` により、キー（シーンID）から {@link MusicSceneId}
 * 型を導出しつつ、各値が正しい形であることをコンパイル時に検証する。
 */
export const MUSIC_MAP = {
  wld_encodia: 'emotional-cinematic_50sec-186759_80Kbps.mp3',
  wld_beginnersPort: 'calm-16213_80Kbps.mp3',
  wld_slumberingBeach: 'on-vacation-125282_80Kbps.mp3',
  wld_silverLeafForest: 'the-old-forest-196807_80Kbps.mp3',
  wld_adventurersCamp: 'forest-time-acoustic-folk-background-music-151424_80Kbps.mp3',
  wld_crystalWaveIsland: 'summer-vibes-158665_80Kbps.mp3',
  cmp_cradle: 'thinking-time-action-bass-loop-225432_80kbps.mp3',
  cmp_arcaneLab: 'ambient-jorney-150878_80kbps.mp3',
  cmp_fairyBroker: 'mysterious-music-light-mystical-background-music-for-short-video-vlog-174918_80Kbps.mp3',
  cmp_emporium: 'mysterious-music-light-mystical-background-music-for-short-video-vlog-174918_80Kbps.mp3',
  goddessFirstOracle: 'documentary-nature-music-15-sec-333707.mp3',
  codexInYourHands: { filename: 'orchestral-win-331233_80Kbps.mp3', loop: false },
} as const satisfies Record<string, string | MusicEntry>

/** {@link MUSIC_MAP} に定義済みのシーンID（リテラル補完・型安全な参照用） */
export type MusicSceneId = keyof typeof MUSIC_MAP
